// src/utils/reportConfig.js
//
// Server-side switches for optional parts of the health report (GET /api/config/report, driven by previu.report.* in the backend's
// application.properties). One module-level snapshot, so the report tabs (React) and the PDF builders (plain functions) read the
// same answer. Everything defaults to OFF until the server has said otherwise: a failed or pending fetch hides the section rather
// than showing something the server turned off.

import { useEffect, useSyncExternalStore } from 'react';
import { reportConfigAPI } from '../services/api';

let config = { clinicalDetailEnabled: false };
let loaded = false;
let inFlight = null;
const listeners = new Set();

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

/** Fetches the switches once per page load (retried on the next call if it failed). Safe to call from anywhere. */
export function loadReportConfig() {
  if (loaded) return Promise.resolve(config);
  if (!inFlight) {
    inFlight = reportConfigAPI.get()
      .then(res => {
        config = { clinicalDetailEnabled: res.data?.clinicalDetailEnabled === true };
        loaded = true;
        listeners.forEach(l => l());
        return config;
      })
      .catch(() => config)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Synchronous read for non-React code (the PDF builders). Also kicks off the fetch if it has not happened yet. */
export function isClinicalDetailEnabled() {
  if (!loaded) loadReportConfig();
  return config.clinicalDetailEnabled;
}

/** { clinicalDetailEnabled } for a component; re-renders when the server's answer arrives. */
export function useReportConfig() {
  useEffect(() => { loadReportConfig(); }, []);
  return useSyncExternalStore(subscribe, () => config);
}
